import React, { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { getActivity } from './activityApi';

const selectClass =
  'h-9 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

// What each recorded action reads as. The subject follows: a candidate, person or job.
const ACTION_TEXT = {
  'candidate.viewed': 'viewed',
  'resume.viewed': 'opened the resume of',
  'candidate.added': 'added',
  'candidate.ai_reviewed': 'ran an AI review for',
  'candidate.shortlisted': 'shortlisted',
  'candidate.rejected': 'rejected',
  'candidate.status_changed': 'changed the status of',
  'candidate.contact_edited': 'edited the contact details of',
  'candidate.edited': 'edited the profile of',
  'candidate.selected': 'selected',
  'candidate.selection_cancelled': 'cancelled the selection of',
  'candidate.withdrawn': 'marked as withdrawn',
  'candidate.joined': 'marked as joined',
  'candidate.hired': 'marked as hired',
  'test.scheduled': 'scheduled a test for',
  'screening.phone_call': 'started an AI phone screen with',
  'screening.video_requested': 'asked for a video resume from',
  'interview.scheduled': 'scheduled an interview with',
  'interview.started': 'started an interview with',
  'interview.slots_requested': 'asked for interview times from',
  'interview.rescheduled': 'rescheduled the interview with',
  'interview.cancelled': 'cancelled the interview with',
  'interview.ended': 'ended the interview with',
  'offer.drafted': 'drafted an offer for',
  'offer.revised': 'revised the offer for',
  'offer.reopened': 'reopened the offer for',
  'offer.released': 'sent the offer to',
  'offer.acceptance_recorded': 'recorded the offer acceptance of',
  'offer.approval_requested': 'asked for approval of the offer for',
  'offer.approved': 'approved the offer for',
  'offer.approval_rejected': 'sent back the offer for',
  'onboarding.joining_date_changed': 'changed the joining date of',
  'job.created': 'posted a job',
  'job.edited': 'edited a job',
  'job.deleted': 'deleted a job',
  'job.closed': 'closed a job',
  'job.visibility_changed': 'changed who can see a job',
  'job.broadcast': 'published a job',
  'job.people_changed': 'changed who works on a job',
  'settings.offer_approvals_changed': 'changed offer approvals',
  'team.invited': 'invited',
  'team.invite_accepted': 'joined the team',
  'team.invite_cancelled': 'cancelled the invite for',
  'team.invite_resent': 'resent the invite to',
  'team.role_changed': 'changed the role of',
  'team.role_created': 'created the role',
  'team.role_edited': 'edited the role',
  'team.role_deleted': 'deleted the role',
  'team.deactivated': 'deactivated',
  'team.reactivated': 'reactivated',
  'team.login_locked': 'was locked out after failed sign-ins',
  'team.password_reset': 'reset their password',
  'team.signed_in': 'signed in',
  'team.created': 'created a team',
  'team.updated': 'updated a team',
  'team.deleted': 'deleted a team',
};

const CATEGORIES = [
  ['', 'All activity'],
  ['candidate', 'Candidates'],
  ['resume', 'Resumes'],
  ['interview', 'Interviews'],
  ['offer', 'Offers'],
  ['onboarding', 'Onboarding'],
  ['test', 'Tests'],
  ['screening', 'Screening'],
  ['job', 'Jobs'],
  ['team', 'Team'],
  ['settings', 'Settings'],
];

const ROLE_NAMES = {
  owner: 'Owner', account_admin: 'Account Admin', cxo: 'CXO', hr_head: 'HR Head', hr_manager: 'HR Manager',
  recruiter: 'Recruiter', hiring_manager: 'Hiring Manager', interviewer: 'Interviewer',
};

// Things a person does to their own account, so the sentence has no subject.
const SELF_ACTIONS = new Set(['team.signed_in', 'team.invite_accepted', 'team.login_locked', 'team.password_reset']);

const humanize = (action) => String(action || '').split('.').pop().replace(/_/g, ' ');

/** The actor's role as people know it. A custom role that was deleted since has no name left. */
export const roleNameOf = (actor) => {
  const key = String(actor?.role || '');
  return ROLE_NAMES[key] || actor?.role_name || (key.startsWith('custom_') ? 'Custom role' : key);
};

/** The sentence after the person's name, e.g. "shortlisted Meera Iyer". */
export const describeActivity = (entry) => {
  const switchedOn = entry.details?.offers_need_approval;
  if (entry.action === 'settings.offer_approvals_changed' && typeof switchedOn === 'boolean') {
    return switchedOn ? 'turned offer approvals on' : 'turned offer approvals off';
  }
  const verb = ACTION_TEXT[entry.action] || humanize(entry.action);
  if (SELF_ACTIONS.has(entry.action)) return verb;
  const subject =
    entry.candidate_name ||
    entry.target?.name ||
    (entry.target?.type === 'candidate' ? entry.target.id : '') ||
    (entry.target?.type === 'role' ? entry.details?.name : '') ||
    '';
  const sentence = subject ? `${verb} ${subject}` : verb;
  return entry.action === 'offer.approval_rejected' && entry.details?.comment
    ? `${sentence}: "${entry.details.comment}"`
    : sentence;
};

const whenLabel = (iso) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

function ActivityRow({ entry }) {
  const who = entry.actor?.name || entry.actor?.email || 'Someone';
  return (
    <li className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-2.5 text-sm">
      <span className="w-36 shrink-0 text-xs tabular-nums text-muted-foreground">{whenLabel(entry.at)}</span>
      <span className="min-w-0 flex-1">
        <span className="font-medium text-foreground">{who}</span>
        <span className="ml-1.5 rounded border px-1.5 py-px text-[11px] text-muted-foreground">
          {roleNameOf(entry.actor)}
        </span>{' '}
        <span className="text-foreground">{describeActivity(entry)}</span>
        {entry.job?.title ? <span className="text-muted-foreground"> · {entry.job.title}</span> : null}
      </span>
    </li>
  );
}

/**
 * Settings → Activity: who did what in the company account, newest first. The
 * server records it; this only reads.
 */
const ActivitySection = () => {
  const [filters, setFilters] = useState({ person: '', category: '', since: '', until: '' });
  const [entries, setEntries] = useState([]);
  const [people, setPeople] = useState([]);
  const [nextBeforeId, setNextBeforeId] = useState(null);
  const [state, setState] = useState('loading');
  const [loadingMore, setLoadingMore] = useState(false);

  const query = useCallback(
    (extra = {}) => ({
      person: filters.person,
      category: filters.category,
      // A date filter covers whole days in the viewer's time zone.
      since: filters.since ? new Date(`${filters.since}T00:00:00`).toISOString() : '',
      until: filters.until ? new Date(`${filters.until}T23:59:59.999`).toISOString() : '',
      ...extra,
    }),
    [filters],
  );

  const load = useCallback(async () => {
    setState('loading');
    try {
      const page = await getActivity(query());
      setEntries(page.activity);
      setNextBeforeId(page.nextBeforeId);
      if (page.people) setPeople(page.people);
      setState('ready');
    } catch (err) {
      setState('error');
    }
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const page = await getActivity(query({ beforeId: nextBeforeId }));
      setEntries((current) => [...current, ...page.activity]);
      setNextBeforeId(page.nextBeforeId);
    } catch (err) {
      setState('error');
    } finally {
      setLoadingMore(false);
    }
  };

  const setFilter = (key) => (event) => setFilters((current) => ({ ...current, [key]: event.target.value }));

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Who did what in your company account: candidates, interviews, offers, jobs and your team.
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <Label htmlFor="activity-person">Person</Label>
          <select id="activity-person" className={selectClass} value={filters.person} onChange={setFilter('person')}>
            <option value="">Everyone</option>
            {people.map((person) => (
              <option key={person.email} value={person.email}>
                {person.name || person.email}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="activity-category">Type</Label>
          <select id="activity-category" className={selectClass} value={filters.category} onChange={setFilter('category')}>
            {CATEGORIES.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="activity-since">From</Label>
          <Input id="activity-since" type="date" className="h-9 w-40" value={filters.since} onChange={setFilter('since')} />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="activity-until">To</Label>
          <Input id="activity-until" type="date" className="h-9 w-40" value={filters.until} onChange={setFilter('until')} />
        </div>
      </div>

      {state === 'loading' && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading activity…
        </div>
      )}

      {state === 'error' && (
        <div className="flex items-center justify-between rounded-lg border px-4 py-3 text-sm">
          <span className="text-muted-foreground">Activity could not be loaded.</span>
          <Button size="sm" variant="outline" onClick={load}>
            Try again
          </Button>
        </div>
      )}

      {state === 'ready' && !entries.length && (
        <div className="rounded-lg border px-4 py-6 text-sm text-muted-foreground">
          Nothing here yet. Actions show up as people work on candidates, interviews and offers.
        </div>
      )}

      {state === 'ready' && entries.length > 0 && (
        <>
          <ul className="divide-y divide-border rounded-lg border bg-card">
            {entries.map((entry) => (
              <ActivityRow key={entry.id} entry={entry} />
            ))}
          </ul>
          {nextBeforeId && (
            <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore}>
              {loadingMore && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              Load more
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default ActivitySection;
